<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { User } from 'lucide-vue-next';
import type { ClientSelection, ScribeSession } from '@/types';
import { useSessionUpdateMutation } from '@/api/sessions/useSessionUpdateMutation';
import ClientPicker from '@/components/ClientPicker.vue';

const props = defineProps<{ session: ScribeSession }>();
const { t, locale } = useI18n();

const sessionUpdate = useSessionUpdateMutation();

const clientName = computed(
  () => props.session.client_snapshot?.client_name ?? '',
);

const initials = computed(() =>
  clientName.value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join(''),
);

const primary = computed(() => clientName.value || t('sessions.addCustomer'));

const secondary = computed(
  () => props.session.title?.trim() || t('sessions.untitled'),
);

const time = computed(() =>
  new Date(props.session.created_at).toLocaleTimeString(locale.value, {
    hour: '2-digit',
    minute: '2-digit',
  }),
);

const dateShort = computed(() =>
  new Date(props.session.created_at).toLocaleDateString(locale.value, {
    day: '2-digit',
    month: 'short',
  }),
);

function onClientSelect(payload: ClientSelection) {
  sessionUpdate.mutate({
    id: props.session.id,
    data: {
      manta_client_id: payload.clientId,
      manta_patient_ids: payload.patientIds,
      client_snapshot: payload.snapshot,
    },
  });
}
</script>

<template>
  <RouterLink
    :to="{ name: 'session-detail', params: { id: session.id } }"
    class="group flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-accent"
    active-class="bg-accent"
  >
    <span
      v-if="clientName"
      :title="clientName"
      class="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
    >
      {{ initials }}
    </span>
    <ClientPicker v-else @select="onClientSelect">
      <button
        type="button"
        :title="t('sessions.addCustomer')"
        class="flex size-9 shrink-0 items-center justify-center rounded-full border border-dashed text-muted-foreground transition-colors hover:border-primary hover:text-primary"
        @click.stop.prevent
      >
        <User class="size-4" />
      </button>
    </ClientPicker>

    <div class="min-w-0 flex-1">
      <span class="block truncate text-sm font-medium">{{ primary }}</span>
      <span class="block truncate text-xs text-muted-foreground">
        {{ secondary }}
      </span>
    </div>

    <div class="shrink-0 text-right leading-tight">
      <div class="text-sm font-semibold tabular-nums">{{ time }}</div>
      <div class="text-xs text-muted-foreground">{{ dateShort }}</div>
    </div>
  </RouterLink>
</template>
